import React from 'react';
import ReactECharts from 'echarts-for-react';
import Loader from 'components/Loader/Loader';
import { ChartProps } from './types';
import { ChartContainer, LoaderWrapper } from './style';
import { measureTextWidth } from './utils/measureText';
import NoDataFound from '../NoDataFound';

const Chart = <T extends object>({
  options,
  isLoading = false,
  topMargin = 20,
  bottomMargin = 20,
  noDataFound = false,
  customLeftMargin = 20,
  testId = 'global-chart',
}: ChartProps<T>) => {
  /**
   * Computes an estimated margin for a given yAxis configuration.
   * It always considers the potential labels, whether coming from the axis data or numeric series values.
   */
  const computeAxisMargin = (axisOptions: any, seriesData?: any[]): number => {
    let rawValues: any[] = [];
    // If the axis explicitly has a data array (e.g., for category axes), use it.
    if (axisOptions && Array.isArray(axisOptions.data)) {
      rawValues = [...axisOptions.data];
    }
    // Otherwise, if series data is available (e.g., for numeric axes), use that.
    else if (seriesData && seriesData.length > 0) {
      rawValues = [...seriesData];
    }

    // Fallback: use a generic value if no data is present.
    if (!rawValues.length) {
      rawValues = [1000]; // Use a number for potential formatting
    }

    let formattedLabels: string[] = [];
    const formatter = axisOptions?.axisLabel?.formatter;

    if (typeof formatter === 'function') {
      // Apply formatter function if it exists
      // Ensure values are numbers if they are intended to be, especially from seriesData
      // The formatter might expect a specific type (e.g., number for numeric axes)
      formattedLabels = rawValues.map((value) => {
        // Attempt to convert to number if it's a string that looks like a number,
        // as formatters for value axes often expect numbers.
        // This is a heuristic and might need adjustment based on typical data.
        const numericValue =
          typeof value === 'string' && !isNaN(parseFloat(value))
            ? parseFloat(value)
            : value;
        return String(formatter(numericValue));
      });
    } else {
      // If no formatter, just convert raw values to string
      formattedLabels = rawValues.map((value) => String(value));
    }

    // Determine font settings (with defaults if not provided).
    const fontSize = axisOptions?.axisLabel?.fontSize || 12;
    const fontFamily = axisOptions?.axisLabel?.fontFamily || 'sans-serif';
    const font = `${fontSize}px ${fontFamily}`;

    // Measure each label's width and determine the maximum.
    let maxWidth = 0;
    formattedLabels.forEach((label) => {
      const width = measureTextWidth(label, font);
      if (width > maxWidth) {
        maxWidth = width;
      }
    });

    // Add extra padding (e.g., customLeftMargin, which defaults to 20px) to ensure nothing gets clipped.
    return Math.ceil(maxWidth) + customLeftMargin;
  };

  // Set default left/right margins.
  let computedLeft = 20;
  let computedRight = 20;

  // Only compute margins if not loading and if data is available.
  if (!isLoading && !noDataFound) {
    // Process the yAxis configuration(s) from options.
    const yAxisConfig = (options as any).yAxis;
    if (yAxisConfig) {
      const yAxes = Array.isArray(yAxisConfig) ? yAxisConfig : [yAxisConfig];
      yAxes.forEach((axis: any) => {
        // When no explicit data is provided for the axis, try to use the series data.
        let seriesData;
        const seriesConfig = (options as any).series;
        if (
          seriesConfig &&
          Array.isArray(seriesConfig) &&
          seriesConfig.length > 0
        ) {
          seriesData = seriesConfig[0].data;
        }

        if (axis?.position === 'left') {
          computedLeft = Math.max(
            computedLeft,
            computeAxisMargin(axis, seriesData)
          );
        } else if (axis?.position === 'right') {
          computedRight = Math.max(
            computedRight,
            computeAxisMargin(axis, seriesData)
          );
        }
      });
    }
  }

  // Merge computed grid margins with any grid settings provided in options.
  const grid = {
    top: topMargin,
    bottom: bottomMargin,
    left: computedLeft,
    right: computedRight,
    ...((options as any).grid || {}),
  };

  // Build the final options object by merging in the computed grid.
  const finalOptions = {
    ...options,
    grid,
  };

  return (
    <ChartContainer
      data-testid={testId}
      data-test-chart-config={
        options?.['chartValues']
          ? JSON.stringify(options['chartValues'])
          : undefined
      }
    >
      {isLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : noDataFound ? (
        <NoDataFound />
      ) : (
        <ReactECharts option={finalOptions} />
      )}
    </ChartContainer>
  );
};

export default Chart;
